(() => {
  const STORAGE_KEYS = {
    platform: 'aw_selected_platform',
    manufacturer: 'aw_selected_manufacturer',
    replicaType: 'aw_selected_replica_type'
  };

  const normalize = (value = '') => String(value)
    .replace(/[₂²]/g, '2')
    .replace(/[₀⁰]/g, '0')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const normalizedList = (values) => (Array.isArray(values) ? values : [])
    .map((value) => normalize(value?.system?.handle || value?.handle || value?.nom?.value || value))
    .filter(Boolean);
  const list = (values) => Array.isArray(values) ? values.filter((value) => String(value ?? '').trim()) : [];
  const clamp = (value) => Math.max(0, Math.min(100, Number(value) || 0));
  const readStorage = (key) => { try { return localStorage.getItem(key) || ''; } catch (_) { return ''; } };
  const writeStorage = (key, value) => {
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch (_) {}
  };

  const init = (root) => {
    if (!root || root.dataset.awReady === 'true') return;

    const dataNode = root.querySelector('[data-aw-product-compatibility]');
    const platformSelect = root.querySelector('[data-aw-platform-select]');
    const manufacturerSelect = root.querySelector('[data-aw-manufacturer-select]');
    const replicaTypeSelect = root.querySelector('[data-aw-replica-type-select]');
    const platformButtons = [...root.querySelectorAll('[data-aw-platform-button]')];
    const result = root.querySelector('[data-aw-result]');
    const title = root.querySelector('[data-aw-title]');
    const message = root.querySelector('[data-aw-message]');
    const icon = root.querySelector('[data-aw-icon]');
    const scoreValue = root.querySelector('[data-aw-score-value]');
    const scoreFill = root.querySelector('[data-aw-score-fill]');
    const scoreLabel = root.querySelector('[data-aw-score-label]');
    const coverageValue = root.querySelector('[data-aw-coverage-value]');
    const coverageFill = root.querySelector('[data-aw-coverage-fill]');
    const coverageLabel = root.querySelector('[data-aw-coverage-label]');
    const coverageHeading = root.querySelector('[data-aw-coverage] .aw-compatibility__coverage-head span');
    const checkerSummary = root.querySelector('[data-aw-checker-summary]');
    const confidence = root.querySelector('[data-aw-confidence]');
    const confidenceValue = root.querySelector('[data-aw-confidence-value]');
    const confidenceLabel = root.querySelector('[data-aw-confidence-label]');
    const confidenceDetail = root.querySelector('[data-aw-confidence-detail]');
    const confidenceProof = root.querySelector('[data-aw-confidence-proof]');

    if (!dataNode || !platformSelect || !manufacturerSelect || !replicaTypeSelect || !result) return;

    let productData;
    try {
      productData = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('[AW Compatibility] Données invalides', error);
      return;
    }

    const supportedPlatforms = normalizedList(productData.platforms);
    const supportedManufacturers = normalizedList(productData.manufacturers);
    const supportedTypes = normalizedList(productData.replicaTypes);
    const hasCompatibilityData = supportedPlatforms.length > 0;
    const rawVerificationStatus = String(productData.verificationStatus || '').trim();
    const verificationStatus = normalize(rawVerificationStatus);
    const verificationScore = Number(productData.confidenceScore);
    const verificationDate = String(productData.verificationDate || '').trim();
    const verifiedStatuses = new Set([
      'verifie', 'verified', 'verifie-aw', 'verified-aw',
      'teste-atelier', 'workshop-tested', 'certifie-atelier', 'workshop-certified'
    ]);
    const testedStatuses = new Set([
      'teste-atelier', 'workshop-tested', 'certifie-atelier', 'workshop-certified'
    ]);
    const documentedStatuses = new Set(['documente', 'documented']);
    const verified = verifiedStatuses.has(verificationStatus)
      && Number.isFinite(verificationScore)
      && verificationScore >= 90
      && Boolean(verificationDate)
      && Boolean(String(productData.message || '').trim());
    const documented = documentedStatuses.has(verificationStatus)
      && Number.isFinite(verificationScore)
      && verificationScore >= 80
      && Boolean(verificationDate)
      && Boolean(String(productData.message || '').trim());
    const physicallyTested = verified && testedStatuses.has(verificationStatus);

    const identityCoverageLists = [
      supportedPlatforms,
      list(productData.manufacturers),
      list(productData.replicaTypes)
    ];
    const technicalCoverageLists = [
      list(productData.rails),
      list(productData.threads),
      list(productData.batteries),
      list(productData.connectors)
    ];
    const identityFilled = identityCoverageLists.filter((items) => items.length > 0).length;
    const technicalFilled = technicalCoverageLists.filter((items) => items.length > 0).length;
    const documentedFamilies = identityFilled + technicalFilled;
    const coveragePercent = Math.round((documentedFamilies / 7) * 100);

    if (coverageHeading) coverageHeading.textContent = 'Données Compatibility documentées';
    if (coverageValue) coverageValue.textContent = `${documentedFamilies} famille${documentedFamilies > 1 ? 's' : ''}`;
    if (coverageFill) coverageFill.style.width = `${coveragePercent}%`;
    if (coverageLabel) {
      coverageLabel.textContent = `Identité ${identityFilled}/3 · technique ${technicalFilled}/4 — les champs non applicables restent volontairement vides.`;
    }

    const paintConfidence = (calculatedScore) => {
      if (!confidence) return;
      const score = (verified || documented) && Number.isFinite(verificationScore) ? clamp(verificationScore) : clamp(calculatedScore);
      confidence.dataset.confidenceStatus = verified ? (physicallyTested ? 'tested' : 'verified') : (documented ? 'documented' : 'calculated');
      if (confidenceValue) confidenceValue.textContent = `${Math.round(score)}%`;

      if (physicallyTested) {
        if (confidenceLabel) confidenceLabel.textContent = 'Testé atelier AW';
        if (confidenceDetail) confidenceDetail.textContent = 'Contrôle physique explicitement enregistré pour cette référence.';
        if (confidenceProof) confidenceProof.textContent = verificationDate ? `Contrôle enregistré le ${verificationDate}.` : 'Preuve de contrôle enregistrée.';
      } else if (verified) {
        if (confidenceLabel) confidenceLabel.textContent = 'Vérifié AW';
        if (confidenceDetail) confidenceDetail.textContent = 'Vérification AW explicitement enregistrée.';
        if (confidenceProof) confidenceProof.textContent = verificationDate ? `Vérification enregistrée le ${verificationDate}.` : 'Preuve de vérification enregistrée.';
      } else if (documented) {
        if (confidenceLabel) confidenceLabel.textContent = 'Documenté AW';
        if (confidenceDetail) confidenceDetail.textContent = 'Preuve et date enregistrées, sans niveau Vérifié AW.';
        if (confidenceProof) confidenceProof.textContent = verificationDate ? `Documentation enregistrée le ${verificationDate}.` : 'Preuve documentaire enregistrée.';
      } else {
        if (confidenceLabel) confidenceLabel.textContent = 'Correspondance calculée AW';
        if (confidenceDetail) confidenceDetail.textContent = 'Indice issu des données Compatibility et de la sélection, sans validation factuelle enregistrée.';
        if (confidenceProof) confidenceProof.textContent = rawVerificationStatus
          ? `Statut actuel : ${rawVerificationStatus}. Cet indice ne prouve pas un contrôle physique.`
          : 'Aucun statut de vérification factuelle n’est enregistré pour cette fiche.';
      }
    };

    const setScore = (score, label) => {
      const safe = clamp(score);
      if (scoreValue) scoreValue.textContent = `${safe}%`;
      if (scoreFill) scoreFill.style.width = `${safe}%`;
      if (scoreLabel) scoreLabel.textContent = label;
      paintConfidence(safe);
    };

    const render = (state, heading, text, symbol, score, scoreText) => {
      result.className = `aw-compatibility__result aw-compatibility__result--${state}`;
      if (title) title.textContent = heading;
      if (message) message.textContent = text;
      if (icon) icon.textContent = symbol;
      setScore(score, scoreText);
    };

    const syncButtons = () => {
      const selected = normalize(platformSelect.value);
      platformButtons.forEach((button) => {
        const active = normalize(button.dataset.awPlatformButton) === selected;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const updateCheckerSummary = () => {
      if (!checkerSummary) return;
      const parts = [];
      if (platformSelect.value) parts.push(platformSelect.options[platformSelect.selectedIndex]?.text || '');
      if (manufacturerSelect.value) parts.push(manufacturerSelect.options[manufacturerSelect.selectedIndex]?.text || '');
      if (replicaTypeSelect.value) parts.push(replicaTypeSelect.options[replicaTypeSelect.selectedIndex]?.text || '');
      checkerSummary.textContent = parts.length ? `Ma réplique : ${parts.join(' · ')}` : 'Vérifier ma réplique';
    };

    const broadcast = () => {
      const detail = {
        platform: platformSelect.value,
        platformLabel: platformSelect.options[platformSelect.selectedIndex]?.text || '',
        manufacturer: manufacturerSelect.value,
        replicaType: replicaTypeSelect.value
      };
      dispatchEvent(new CustomEvent('aw:platform-changed', { detail }));
      dispatchEvent(new CustomEvent('aw:compatibility-changed', { detail }));
    };

    const evaluate = (shouldBroadcast = true) => {
      const platform = normalize(platformSelect.value);
      const manufacturer = normalize(manufacturerSelect.value);
      const replicaType = normalize(replicaTypeSelect.value);

      writeStorage(STORAGE_KEYS.platform, platformSelect.value);
      writeStorage(STORAGE_KEYS.manufacturer, manufacturerSelect.value);
      writeStorage(STORAGE_KEYS.replicaType, replicaTypeSelect.value);
      syncButtons();
      updateCheckerSummary();

      if (!platform) {
        render(
          productData.initialState || 'documented',
          productData.initialTitle || 'Compatibilité documentée',
          productData.initialMessage || 'Consultez les plateformes référencées ou vérifiez votre réplique.',
          productData.initialIcon || '✓',
          0,
          'Sélectionnez votre réplique pour calculer la correspondance'
        );
        if (shouldBroadcast) broadcast();
        return;
      }

      if (!hasCompatibilityData) {
        render('unknown', 'À vérifier avant commande', 'Les données disponibles ne permettent pas de confirmer cette plateforme.', '?', 35, 'Correspondance insuffisamment documentée');
        if (shouldBroadcast) broadcast();
        return;
      }

      if (!supportedPlatforms.includes(platform)) {
        render('unknown', 'À vérifier avant commande', 'Cette plateforme n’est pas référencée pour ce produit.', '?', 25, 'Plateforme non référencée');
        if (shouldBroadcast) broadcast();
        return;
      }

      let score = 100;
      const notes = [];

      if (manufacturer) {
        if (supportedManufacturers.length && !supportedManufacturers.includes(manufacturer)) {
          score -= 25;
          notes.push('fabricant non confirmé');
        } else if (!supportedManufacturers.length) {
          score -= 10;
          notes.push('fabricant à vérifier');
        }
      } else {
        score -= 10;
        notes.push('fabricant non sélectionné');
      }

      if (replicaType) {
        if (supportedTypes.length && !supportedTypes.includes(replicaType)) {
          score -= 35;
          notes.push('type de réplique non confirmé');
        } else if (!supportedTypes.length) {
          score -= 10;
          notes.push('type de réplique à vérifier');
        }
      } else {
        score -= 10;
        notes.push('type de réplique non sélectionné');
      }

      const needsWorkshop = productData.workshopInstallation === true;
      const level = normalize(productData.installationLevel);
      const technical = needsWorkshop || ['technique', 'difficile', 'expert', 'complexe'].includes(level);

      if (technical) {
        score = Math.min(score, 80);
        notes.push('montage technique');
      }

      if (technical && score >= 60) {
        render(
          'conditional',
          'Compatible sous conditions',
          needsWorkshop ? 'Plateforme référencée. Montage technique : l’Atelier AW est recommandé.' : 'Plateforme référencée. Un montage technique est à prévoir.',
          '!',
          score,
          'Compatibilité plateforme confirmée · montage à contrôler'
        );
      } else if (score >= 95 && verified) {
        render('compatible', 'Compatible avec votre plateforme', 'La plateforme et le profil sélectionnés correspondent aux données AW vérifiées.', '✓', score, 'Vérification AW enregistrée');
      } else if (score >= 95) {
        render('conditional', 'Compatible à confirmer', 'La plateforme et le profil correspondent aux données AW référencées. Consultez la preuve avant commande.', '!', score, 'Correspondance calculée — non vérifiée');
      } else if (score >= 60) {
        render('conditional', 'Compatible sous conditions', notes.length ? `Plateforme référencée. À contrôler : ${notes.join(', ')}.` : 'La plateforme est référencée, avec des points à contrôler.', '!', score, 'Vérification complémentaire requise');
      } else {
        render('unknown', 'À vérifier avant commande', 'La plateforme est référencée, mais le fabricant ou le type de réplique nécessite une validation.', '?', score, 'Validation technique nécessaire');
      }

      if (shouldBroadcast) broadcast();
    };

    platformButtons.forEach((button) => button.addEventListener('click', () => {
      platformSelect.value = button.dataset.awPlatformButton || '';
      evaluate();
    }));
    [platformSelect, manufacturerSelect, replicaTypeSelect].forEach((control) => {
      control.addEventListener('change', () => evaluate());
    });

    const storedPlatform = readStorage(STORAGE_KEYS.platform);
    const storedManufacturer = readStorage(STORAGE_KEYS.manufacturer);
    const storedReplicaType = readStorage(STORAGE_KEYS.replicaType);

    if ([...platformSelect.options].some((option) => option.value === storedPlatform)) platformSelect.value = storedPlatform;
    if ([...manufacturerSelect.options].some((option) => option.value === storedManufacturer)) manufacturerSelect.value = storedManufacturer;
    if ([...replicaTypeSelect.options].some((option) => option.value === storedReplicaType)) replicaTypeSelect.value = storedReplicaType;

    addEventListener('aw:platform-reset', () => {
      platformSelect.value = '';
      writeStorage(STORAGE_KEYS.platform, '');
      evaluate(false);
    });

    root.dataset.awReady = 'true';
    evaluate();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-aw-compatibility]').forEach(init);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
